/**
 * Utility functions for converting and formatting units based on user preferences
 */

export type TemperatureUnit = "celsius" | "fahrenheit";
export type WindSpeedUnit = "mph" | "kmh" | "ms" | "knots";

/**
 * @deprecated Use TimeFormat from @/lib/timezone instead
 * Kept for backward compatibility
 */
export type TimeFormat = "12h" | "24h";

/**
 * Convert temperature from Celsius to user's preferred unit
 */
export function formatTemperature(celsius: number, unit: TemperatureUnit = "fahrenheit"): string {
  if (unit === "celsius") {
    return `${Math.round(celsius)}°C`;
  }
  const fahrenheit = (celsius * 9/5) + 32;
  return `${Math.round(fahrenheit)}°F`;
}

/**
 * Convert wind speed from km/h to user's preferred unit
 */
export function formatWindSpeed(kmh: number, unit: WindSpeedUnit = "mph"): string {
  switch (unit) {
    case "mph": {
      const mph = kmh * 0.621371;
      return `${Math.round(mph)} mph`;
    }
    case "ms": {
      const ms = kmh / 3.6;
      return `${ms.toFixed(1)} m/s`;
    }
    case "knots": {
      const knots = kmh * 0.539957;
      return `${knots.toFixed(1)} kn`;
    }
    case "kmh":
    default:
      return `${Math.round(kmh)} km/h`;
  }
}

/**
 * Format time based on user's preferred format
 */
export function formatTime(date: Date, format: TimeFormat = "12h"): string {
  if (format === "24h") {
    const result = date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    // Normalize "24:00" to "00:00" for midnight (some locales return 24:00)
    return result.replace(/^24:/, "00:");
  }
  return date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/**
 * Format date and time based on user's preferred format
 */
export function formatDateTime(date: Date, format: TimeFormat = "12h"): string {
  const dateStr = date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
  const timeStr = formatTime(date, format);
  return `${dateStr} at ${timeStr}`;
}

/**
 * Get unit label for temperature
 */
export function getTemperatureLabel(unit: TemperatureUnit): string {
  return unit === "celsius" ? "°C" : "°F";
}

/**
 * Get unit label for wind speed
 */
export function getWindSpeedLabel(unit: WindSpeedUnit): string {
  switch (unit) {
    case "mph":
      return "mph";
    case "ms":
      return "m/s";
    case "knots":
      return "kn";
    case "kmh":
    default:
      return "km/h";
  }
}
